/* Gorilla Chalk theme JS — vanilla, no dependencies.
   Handles: scroll reveal, hero parallax, bottle flip, FAQ accordion,
   Ajax cart drawer, discount popup. No customer data is stored or logged. */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Money formatting ---------- */
  var moneyFormat = (document.body.getAttribute('data-money-format') || '${{amount}}');
  function formatMoney(cents) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];
    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, parts[0])
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','));
  }

  /* ---------- Scroll reveal ---------- */
  function initReveal() {
    var els = document.querySelectorAll('.reveal:not([data-reveal-ready])');
    els.forEach(function (el) { el.setAttribute('data-reveal-ready', 'true'); });
    if (!('IntersectionObserver' in window) || reducedMotion) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -100px 0px' });
    els.forEach(function (el) {
      var delay = el.getAttribute('data-delay');
      if (delay) el.style.setProperty('--reveal-delay', delay + 's');
      io.observe(el);
    });
  }

  /* ---------- Hero parallax ---------- */
  function initParallax() {
    if (reducedMotion) return;
    var ticking = false;
    function update() {
      ticking = false;
      var bg = document.querySelector('[data-hero-parallax]');
      if (!bg) return;
      var doc = document.documentElement;
      var max = doc.scrollHeight - window.innerHeight;
      var progress = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
      bg.style.transform = 'translateY(' + (progress * 50) + '%)';
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  /* ---------- Smooth scroll for [data-scroll-to] ---------- */
  function initScrollButtons() {
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-scroll-to]');
      if (!trigger) return;
      var selector = trigger.getAttribute('data-scroll-to') || '';
      var target = selector.charAt(0) === '#' ? document.getElementById(selector.slice(1)) : null;
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth' });
      } else if (/^#[a-zA-Z][\w-]*$/.test(selector)) {
        e.preventDefault();
        var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
        window.location.assign(root + selector);
      }
    });
  }

  /* ---------- Bottle flip ---------- */
  function initBottles() {
    document.querySelectorAll('[data-bottle]:not([data-bottle-ready])').forEach(function (bottle) {
      if (bottle.closest('[data-product-options]') || bottle.hasAttribute('data-product-gallery')) return;
      bottle.setAttribute('data-bottle-ready', 'true');
      bottle.addEventListener('mouseenter', function () { bottle.classList.add('is-flipped'); });
      bottle.addEventListener('mouseleave', function () { bottle.classList.remove('is-flipped'); });
      bottle.addEventListener('click', function () { bottle.classList.toggle('is-flipped'); });
      bottle.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          bottle.classList.toggle('is-flipped');
        }
      });
    });
  }

  /* ---------- FAQ accordion ---------- */
  function initFaq() {
    document.querySelectorAll('[data-faq]').forEach(function (root) {
      if (root.dataset.faqReady) return;
      root.dataset.faqReady = 'true';
      var triggers = root.querySelectorAll('.faq-item__trigger');
      triggers.forEach(function (trigger) {
        trigger.addEventListener('click', function () {
          var panel = document.getElementById(trigger.getAttribute('aria-controls'));
          var isOpen = trigger.getAttribute('aria-expanded') === 'true';
          // close others (single-open behaviour, like the original)
          triggers.forEach(function (other) {
            if (other !== trigger && other.getAttribute('aria-expanded') === 'true') {
              other.setAttribute('aria-expanded', 'false');
              var otherPanel = document.getElementById(other.getAttribute('aria-controls'));
              collapse(otherPanel);
            }
          });
          trigger.setAttribute('aria-expanded', String(!isOpen));
          if (isOpen) { collapse(panel); } else { expand(panel); }
        });
      });
    });
    function expand(panel) {
      panel.style.height = panel.scrollHeight + 'px';
      panel.addEventListener('transitionend', function done() {
        if (panel.previousElementSibling.querySelector('[aria-expanded="true"]')) panel.style.height = 'auto';
        panel.removeEventListener('transitionend', done);
      });
    }
    function collapse(panel) {
      panel.style.height = panel.scrollHeight + 'px';
      // force reflow so the transition runs
      void panel.offsetHeight;
      panel.style.height = '0px';
    }
  }

  /* ---------- Cart drawer (Shopify Ajax Cart API) ---------- */
  var cartDrawer = document.querySelector('[data-cart-drawer]');
  var cartBackdrop = document.querySelector('[data-cart-backdrop]');
  var cartBody = cartDrawer ? cartDrawer.querySelector('[data-cart-body]') : null;
  var cartFooter = cartDrawer ? cartDrawer.querySelector('[data-cart-footer]') : null;
  var cartSubtotalEl = cartDrawer ? cartDrawer.querySelector('[data-cart-subtotal]') : null;
  var cartCountEls = document.querySelectorAll('[data-cart-count]');
  var lastFocused = null;
  var cartBusy = false;
  var latestCart = null;

  function refreshCartElements() {
    cartDrawer = document.querySelector('[data-cart-drawer]');
    cartBackdrop = document.querySelector('[data-cart-backdrop]');
    cartBody = cartDrawer ? cartDrawer.querySelector('[data-cart-body]') : null;
    cartFooter = cartDrawer ? cartDrawer.querySelector('[data-cart-footer]') : null;
    cartSubtotalEl = cartDrawer ? cartDrawer.querySelector('[data-cart-subtotal]') : null;
    cartCountEls = document.querySelectorAll('[data-cart-count]');
  }

  function updateCount(count) {
    cartCountEls.forEach(function (el) {
      el.textContent = String(count);
      if (count > 0) { el.removeAttribute('hidden'); } else { el.setAttribute('hidden', ''); }
    });
  }

  function svgIcon(name) {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', name === 'minus' ? 'M5 12h14' : 'M5 12h14M12 5v14');
    svg.appendChild(path);
    return svg;
  }

  /* Renders the cart with DOM APIs + textContent only (no innerHTML with
     dynamic data), so product titles can never inject markup. */
  function renderCart(cart) {
    latestCart = cart;
    if (!cartBody) return;
    updateCount(cart.item_count);
    cartBody.textContent = '';

    if (!cart.items || cart.items.length === 0) {
      var empty = document.createElement('div');
      empty.className = 'cart-drawer__empty';
      empty.textContent = cartDrawer.getAttribute('data-empty-text') || 'Your cart is empty.';
      cartBody.appendChild(empty);
      if (cartFooter) cartFooter.setAttribute('hidden', '');
      return;
    }

    cart.items.forEach(function (item) {
      var line = document.createElement('div');
      line.className = 'cart-line';

      var media = document.createElement('div');
      media.className = 'cart-line__media';
      var mappedImage = window.GorillaProductOptions && window.GorillaProductOptions.getImageForVariant(item.variant_id);
      var imageUrl = mappedImage || item.image;
      if (imageUrl) {
        var img = document.createElement('img');
        img.src = imageUrl;
        img.alt = item.product_title || '';
        img.width = 64;
        img.height = 64;
        img.loading = 'lazy';
        media.appendChild(img);
      }
      line.appendChild(media);

      var info = document.createElement('div');
      info.className = 'cart-line__info';

      var top = document.createElement('div');
      var title = document.createElement('h4');
      title.className = 'cart-line__title';
      title.textContent = item.product_title || '';
      top.appendChild(title);
      if (item.variant_title && item.variant_title !== 'Default Title') {
        var variant = document.createElement('div');
        variant.className = 'cart-line__variant';
        variant.textContent = item.variant_title;
        top.appendChild(variant);
      }
      info.appendChild(top);

      var row = document.createElement('div');
      row.className = 'cart-line__row';

      var price = document.createElement('div');
      price.className = 'cart-line__price';
      price.textContent = formatMoney(item.final_price);
      row.appendChild(price);

      var controls = document.createElement('div');
      controls.className = 'cart-line__controls';

      var qty = document.createElement('div');
      qty.className = 'cart-qty';
      var minus = document.createElement('button');
      minus.type = 'button';
      minus.setAttribute('aria-label', 'Decrease quantity');
      minus.appendChild(svgIcon('minus'));
      minus.addEventListener('click', function () { changeLine(item.key, item.quantity - 1); });
      var value = document.createElement('span');
      value.className = 'cart-qty__value';
      value.textContent = String(item.quantity);
      var plus = document.createElement('button');
      plus.type = 'button';
      plus.setAttribute('aria-label', 'Increase quantity');
      plus.appendChild(svgIcon('plus'));
      plus.addEventListener('click', function () { changeLine(item.key, item.quantity + 1); });
      qty.appendChild(minus);
      qty.appendChild(value);
      qty.appendChild(plus);
      controls.appendChild(qty);

      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'cart-line__remove';
      remove.textContent = cartDrawer.getAttribute('data-remove-text') || 'Remove';
      remove.addEventListener('click', function () { changeLine(item.key, 0); });
      controls.appendChild(remove);

      row.appendChild(controls);
      info.appendChild(row);
      line.appendChild(info);
      cartBody.appendChild(line);
    });

    if (cartFooter) cartFooter.removeAttribute('hidden');
    if (cartSubtotalEl) cartSubtotalEl.textContent = formatMoney(cart.total_price);
    var discounts = cartDrawer.querySelector('[data-cart-discounts]');
    if (discounts) {
      discounts.textContent = (cart.cart_level_discount_applications || []).map(function (discount) {
        return discount.title + ' (−' + formatMoney(discount.total_allocated_amount) + ')';
      }).join(', ');
    }
  }

  function setBusy(busy) {
    cartBusy = busy;
    if (cartDrawer) cartDrawer.classList.toggle('is-loading', busy);
    if (cartDrawer) {
      cartDrawer.setAttribute('aria-busy', String(busy));
      cartDrawer.querySelectorAll('.cart-qty button, .cart-line__remove, [name="checkout"]').forEach(function (btn) { btn.disabled = busy; });
    }
    document.documentElement.dataset.cartBusy = String(busy);
  }

  function fetchCart() {
    return fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart.js', {
      headers: { 'Accept': 'application/json' }
    }).then(function (r) {
      if (!r.ok) throw new Error('Cart request failed');
      return r.json();
    });
  }

  function changeLine(key, quantity) {
    if (cartBusy) return;
    setBusy(true);
    fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Cart update failed');
        return r.json();
      })
      .then(renderCart)
      .catch(function () { announceError(); })
      .finally(function () { setBusy(false); });
  }

  function openCart(refresh) {
    if (!cartDrawer) return;
    if (!cartDrawer.classList.contains('is-open')) lastFocused = document.activeElement;
    cartDrawer.classList.add('is-open');
    if (cartBackdrop) cartBackdrop.classList.add('is-open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    var closeBtn = cartDrawer.querySelector('[data-cart-close]');
    if (closeBtn) closeBtn.focus();
    if (refresh !== false) fetchCart().then(renderCart).catch(announceError);
  }

  function closeCart() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    if (cartBackdrop) cartBackdrop.classList.remove('is-open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  function announceError() {
    var region = document.querySelector('[data-cart-error]');
    if (region) {
      region.textContent = region.getAttribute('data-message') || 'Could not update cart. Please try again.';
      window.setTimeout(function () { region.textContent = ''; }, 4000);
    }
  }

  function addItems(items) {
    if (cartBusy || !Array.isArray(items) || !items.length) return;
    if (!items.every(function (item) {
      return /^\d+$/.test(String(item.id)) && Number.isInteger(item.quantity) && item.quantity > 0;
    })) return;
    setBusy(true);
    fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) throw new Error('Add to cart failed');
      return fetchCart();
    }).then(function (cart) {
      renderCart(cart);
      openCart(false);
    }).catch(function () {
      openCart(false);
      announceError();
      // Shopify can partially accept multi-item adds. Reconcile, never blindly retry.
      return fetchCart().then(renderCart).catch(announceError);
    }).finally(function () { setBusy(false); });
  }

  function initCart() {
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-cart-open]')) { e.preventDefault(); openCart(); return; }
      if (e.target.closest('[data-cart-close], [data-cart-backdrop]')) { closeCart(); return; }
      var btn = e.target.closest('[data-add-to-cart]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      var items;
      try { items = JSON.parse(btn.getAttribute('data-add-to-cart')); } catch (err) { return; }
      addItems(items);
    });
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-native-product-form]');
      if (!form || !cartDrawer) return;
      e.preventDefault();
      if (form.querySelector('[type="submit"]:disabled')) return;
      var data = new FormData(form);
      var item = { id: data.get('id'), quantity: Number(data.get('quantity') || 1) };
      // Preserve app-provided line properties and selling plans on native product forms.
      var properties = Object.create(null);
      data.forEach(function (value, key) {
        var match = /^properties\[(.+)\]$/.exec(key);
        if (match && typeof value === 'string') properties[match[1]] = value;
      });
      item.properties = properties;
      if (data.get('selling_plan')) item.selling_plan = data.get('selling_plan');
      addItems([item]);
    });
    document.addEventListener('keydown', function (e) {
      if (!cartDrawer || !cartDrawer.classList.contains('is-open')) return;
      if (e.key === 'Escape') closeCart();
      if (e.key === 'Tab') {
        var focusable = Array.from(cartDrawer.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled)'))
          .filter(function (el) { return el.getClientRects().length > 0; });
        var first = focusable[0], last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
    fetchCart().then(function (cart) { latestCart = cart; updateCount(cart.item_count); }).catch(function () {});
  }

  /* ---------- Discount popup ---------- */
  function initPopup() {
    var popup = document.querySelector('[data-discount-popup]');
    if (!popup) return;
    if (popup.dataset.popupReady) return;
    popup.dataset.popupReady = 'true';
    var STORAGE_KEY = 'gorilla_discount_dismissed';
    var designMode = Boolean(window.Shopify && window.Shopify.designMode);
    var opener = document.querySelector('[aria-controls="' + popup.id + '"][data-popup-open]');
    var timer;
    var delay = parseInt(popup.getAttribute('data-delay'), 10);
    if (isNaN(delay)) delay = 6;

    var dismissed = false;
    try { dismissed = window.localStorage.getItem(STORAGE_KEY) === 'true'; } catch (err) { /* private mode */ }

    // Shopify redirects back with ?customer_posted=true after the native
    // customer form submits successfully.
    var showError = Boolean(popup.querySelector('[data-popup-error]'));
    var showSuccess = !showError && (Boolean(popup.querySelector('[data-popup-posted]')) ||
      /(^|[?&])customer_posted=true(&|$)/.test(window.location.search));

    var signupView = popup.querySelector('[data-popup-signup]');
    var successView = popup.querySelector('[data-popup-success]');

    function open(moveFocus) {
      window.clearTimeout(timer);
      popup.classList.add('is-open');
      popup.removeAttribute('aria-hidden');
      if (opener) { opener.hidden = true; opener.setAttribute('aria-expanded', 'true'); }
      if (moveFocus === true) {
        var target = popup.querySelector(successView && !successView.hidden ? '[data-popup-close]' : 'input[type="email"]');
        if (target) target.focus();
      }
    }
    function dismiss() {
      window.clearTimeout(timer);
      var restoreFocus = popup.contains(document.activeElement);
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      if (opener) {
        opener.hidden = false;
        opener.setAttribute('aria-expanded', 'false');
        if (restoreFocus) opener.focus();
      }
      if (!designMode) {
        try { window.localStorage.setItem(STORAGE_KEY, 'true'); } catch (err) { /* ignore */ }
      }
    }

    if (opener) {
      opener.hidden = false;
      opener.addEventListener('click', function () { open(true); });
    }
    popup.querySelectorAll('[data-popup-close]').forEach(function (btn) {
      btn.addEventListener('click', dismiss);
    });
    function onKeydown(event) {
      if (event.key === 'Escape' && popup.classList.contains('is-open')) dismiss();
    }
    function onSectionSelect(event) {
      if (event.target.contains(popup)) open();
    }
    function onSectionUnload(event) {
      if (!event.target.contains(popup)) return;
      window.clearTimeout(timer);
      document.removeEventListener('keydown', onKeydown);
      document.removeEventListener('shopify:section:select', onSectionSelect);
      document.removeEventListener('shopify:section:unload', onSectionUnload);
    }
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('shopify:section:select', onSectionSelect);
    document.addEventListener('shopify:section:unload', onSectionUnload);

    if (showSuccess) {
      // Show the discount code view and remember the popup was handled.
      if (signupView) signupView.setAttribute('hidden', '');
      if (successView) successView.removeAttribute('hidden');
      if (!designMode) {
        try { window.localStorage.setItem(STORAGE_KEY, 'true'); } catch (err) { /* ignore */ }
      }
      open();
      return;
    }

    if (designMode || showError) { open(); return; }
    if (dismissed) return;
    timer = window.setTimeout(open, delay * 1000);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initReveal();
    initParallax();
    initScrollButtons();
    initBottles();
    initFaq();
    initCart();
    initPopup();
  });
  document.addEventListener('shopify:section:load', function () {
    refreshCartElements();
    if (latestCart) renderCart(latestCart);
    initReveal();
    initFaq();
    initPopup();
  });
})();
